import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, openAuthedFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { AIPanel } from '@/components/AIPanel';
import { SignaturePad } from '@/components/SignaturePad';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, RotateCcw, FileText, Send, ShieldCheck, Download, AlertCircle, ScanLine, Ban } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useApp } from '@/context/AppContext';
import { ScanPagesRow } from '@/components/EmployerFormScan';

function Section({ title, children }) {
  return <Card><CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent>{children}</CardContent></Card>;
}

function AddendumPreview({ sid, employerForm }) {
  const [preview, setPreview] = useState(null);
  const extracted = employerForm?.status === 'extracted';

  useEffect(() => {
    if (extracted) {
      api.get(`/submissions/${sid}/employer-form/preview`).then(r => setPreview(r.data)).catch(() => setPreview(null));
    } else {
      setPreview(null);
    }
  }, [sid, extracted, employerForm]);

  if (!extracted) return null;
  const items = preview?.items || [];
  const unanswered = items.filter(i => i.source === 'unanswered').length;

  return (
    <Card data-testid="doctor-addendum-preview">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <ScanLine className="h-4 w-4 text-primary" /> Employer Form Addendum
          <Badge variant="secondary" className="text-xs">{preview?.form_title || employerForm.form_title}</Badge>
          {unanswered > 0 && <Badge variant="destructive" className="text-xs" data-testid="addendum-unanswered-count">{unanswered} unanswered</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-2">
          The signed PDF fills in the employer's own form and answers every question on it, point by point;
          anything that cannot be entered on the form goes on one extra sheet. Answers are composed from this record — review before signing.
        </p>
        <div className="mb-2">
          <ScanPagesRow mode="staff" sid={sid} employerForm={employerForm} />
        </div>
        {!preview ? (
          <div className="text-sm text-muted-foreground">Loading preview…</div>
        ) : (
          <ul className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="addendum-items">
            {items.map((it) => (
              <li key={it.id} className="rounded border bg-card px-2.5 py-1.5 text-xs">
                <div className="text-muted-foreground">{it.question}</div>
                <div className="mt-0.5 flex items-start gap-1.5">
                  <Badge variant={it.source === 'unanswered' ? 'destructive' : 'outline'} className="text-xs shrink-0 mt-0.5">{it.source}</Badge>
                  <span className="font-medium">{it.answer}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function DoctorReview() {
  const { sid } = useParams();
  const { practice, packetTypes, user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [attest, setAttest] = useState(false);
  const [ackRevisions, setAckRevisions] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReasons, setDeclineReasons] = useState([]);
  const [declineReason, setDeclineReason] = useState('');
  const [declineNote, setDeclineNote] = useState('');

  const load = useCallback(async () => {
    const r = await api.get(`/submissions/${sid}`);
    setData(r.data);
  }, [sid]);
  useEffect(() => { load(); }, [load]);
  // Fetched rather than hard-coded: the reason list is server config, so a change
  // to it reaches this picker on the next page load instead of the next deploy.
  useEffect(() => {
    api.get('/doctor/decline-reasons').then((r) => setDeclineReasons(r.data.reasons || [])).catch(() => setDeclineReasons([]));
  }, []);

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const sub = data.submission;
  const pt = packetTypes.find(p => p.id === sub.packet_type_id) || {};
  // ADR 0009: a question the doctor put to the patient rides the revision-request
  // thread, but it is the doctor's own, so it is shown apart and needs no acknowledging.
  const openRevisions = (sub.revision_requests || []).filter((r) => r.status === 'open' && r.category !== 'doctor_question');
  const openQuestions = (sub.revision_requests || []).filter((r) => r.status === 'open' && r.category === 'doctor_question');
  // What the server will accept, mirrored so no button leads to a 409: a decision
  // only on a case in (or returned from) the doctor's queue, and a re-signature
  // on a delivered packet only while a change request is open (R21, rule 5).
  const decidable = ['awaiting_doctor', 'returned'].includes(sub.status);
  const amending = ['complete', 'amended'].includes(sub.status) && openRevisions.length > 0;
  // With no staff step, "return" puts the question to the patient instead (ADR 0009).
  const askPatient = !!practice?.settings?.direct_to_doctor && sub.status === 'awaiting_doctor';

  const generate = async ({ doctor_note, mode } = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/submissions/${sid}/ai/generate`, { doctor_note, mode });
      toast.success(`AI generated (mode: ${r.data.mode})${r.data.output?._warning ? ' — mock fallback used' : ''}`);
      load();
    } catch (e) { toast.error('AI generation failed'); }
    finally { setBusy(false); }
  };

  const updateAi = async (next) => {
    setData(d => ({ ...d, submission: { ...d.submission, ai_recommendations: next } }));
  };

  const saveAi = async () => {
    await api.patch(`/submissions/${sid}/ai`, { ai_recommendations: sub.ai_recommendations });
    toast.success('AI edits saved');
  };

  const decline = async () => {
    if (!declineReason) { toast.error('Pick a reason'); return; }
    if (declineReasons.find((r) => r.code === declineReason)?.note_required && !declineNote.trim()) {
      toast.error('Say briefly why, so the care team can explain it to the patient.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${sid}/decline`, { reason_code: declineReason, note: declineNote.trim() });
      toast.success('Recorded — the care team has been notified to contact the patient.');
      navigate('/doctor');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record that'); }
    finally { setBusy(false); }
  };

  const sendBack = async () => {
    if (!returnNote.trim()) { toast.error('Comment required'); return; }
    try {
      const r = await api.post(`/submissions/${sid}/return-to-nurse`, { comment: returnNote });
      if (r.data.sent_to === 'patient') {
        toast.success('Sent to the patient — the case stays in your queue until they answer');
        setReturnNote('');
        setReturnOpen(false);
        load();
        return;
      }
      toast.success('Returned to medical staff');
      navigate('/doctor');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send that'); }
  };

  const sign = async () => {
    if (!signatureDataUrl || !attest) { toast.error('Signature + attestation required'); return; }
    if (openRevisions.length > 0 && !ackRevisions) { toast.error('Acknowledge the open patient change request(s) first.'); return; }
    setBusy(true);
    try {
      await api.patch(`/submissions/${sid}/ai`, { ai_recommendations: sub.ai_recommendations || {} });
      const r = await api.post(`/submissions/${sid}/sign`, { signature_data_url: signatureDataUrl, confirm: true, save_to_template: saveTemplate, acknowledge_revisions: ackRevisions });
      // The toast at signing time is the one delivery readout anyone actually
      // reads during a demo, so a simulated entry must not read as a real send.
      const deliverySummary = (r.data.delivery_log || [])
        .map(d => (d.simulated ? `${d.event} (simulated)` : d.event))
        .join(', ');
      toast.success(`Signed. Delivery: ${deliverySummary}`);
      load();
      setSignOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sign failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/doctor')} data-testid="doctor-review-back"><ArrowLeft className="h-4 w-4 mr-1" /> Queue</Button>
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">{sub.patient_form?.full_name || 'Submission'}</h1>
        <StatusBadge status={sub.status} overdue={sub._overdue} />
        <div className="flex-1" />
        {(sub.pdf_file_id || sub.pdf_path) && (
          <Button variant="outline" onClick={() => openAuthedFile(`/submissions/${sid}/pdf`, 'signed_packet.pdf')} data-testid="doctor-download-pdf"><Download className="h-4 w-4 mr-1" /> Download PDF</Button>
        )}
        {/* A declined case is terminal: nothing goes back to the nurse, and nothing
            gets signed. Only the record of the decision remains. */}
        {decidable && (
          <Dialog open={returnOpen} onOpenChange={setReturnOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="doctor-return-to-nurse-button"><RotateCcw className="h-4 w-4 mr-1" /> {askPatient ? 'Ask the patient' : 'Return to medical staff'}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{askPatient ? 'Ask the patient' : 'Return to medical staff'}</DialogTitle>
                <DialogDescription>{askPatient
                  ? 'Your practice sends cases straight to you, so this goes to the patient. They get a link (never your question itself) and answer on their secure page. The case stays in your queue.'
                  : 'Add a comment so the medical staff knows what additional information is needed.'}</DialogDescription>
              </DialogHeader>
              <Textarea placeholder={askPatient ? 'What do you need to know? (required)' : "Comment explaining what's needed (required)"} value={returnNote} onChange={(e) => setReturnNote(e.target.value)} data-testid="doctor-return-comment" />
              <DialogFooter><Button onClick={sendBack} data-testid="doctor-return-confirm">{askPatient ? 'Send to patient' : 'Return'}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {decidable && (
          <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="doctor-decline-button"><Ban className="h-4 w-4 mr-1" /> Unable to certify</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Record that you are not certifying</DialogTitle>
                <DialogDescription>
                  This closes the case with your decision on file. The care team is notified to
                  contact the patient; the patient is told there is an update, never your reason.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Reason</label>
                  <Select value={declineReason} onValueChange={setDeclineReason}>
                    <SelectTrigger className="mt-1" data-testid="doctor-decline-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                    <SelectContent>
                      {declineReasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Note for the care team {declineReasons.find((r) => r.code === declineReason)?.note_required ? '(required)' : '(optional)'}</label>
                  <Textarea className="mt-1" maxLength={2000} placeholder="What should the team tell the patient?" value={declineNote} onChange={(e) => setDeclineNote(e.target.value)} data-testid="doctor-decline-note" />
                </div>
                <p className="text-xs text-muted-foreground">
                  A case is charged for when it reaches your queue, so this decision costs the
                  practice exactly what signing would — deliberately, so that price never leans
                  on a clinical judgement.
                </p>
              </div>
              <DialogFooter>
                <Button variant="destructive" onClick={decline} disabled={busy || !declineReason} data-testid="doctor-decline-confirm">Record decision</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {(decidable || amending) && (
          <Dialog open={signOpen} onOpenChange={setSignOpen}>
            <DialogTrigger asChild>
              <Button data-testid="doctor-finalize-signature-button"><ShieldCheck className="h-4 w-4 mr-1" /> {amending ? 'Sign amended packet' : 'Sign & finalize'}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Provider Signature — {pt.label}</DialogTitle><DialogDescription>Review, attest, sign, and confirm delivery method for this packet.</DialogDescription></DialogHeader>
              <Accordion type="single" collapsible defaultValue="review">
                <AccordionItem value="review">
                  <AccordionTrigger>1. Final review</AccordionTrigger>
                  <AccordionContent className="text-sm space-y-1">
                    <div>Patient: <strong>{sub.patient_form?.full_name}</strong></div>
                    <div>Packet: <strong>{pt.label}</strong></div>
                    <div>AI source: <strong>{sub.ai_recommendations?.source_used || '—'}</strong></div>
                    {sub.employer_form?.status === 'extracted' && (
                      <div data-testid="sign-review-addendum">Employer form addendum: <strong>{(sub.employer_form.questions || []).length} questions answered ({sub.employer_form.form_title})</strong></div>
                    )}
                    {openRevisions.length > 0 && (
                      <div className="rounded-md border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-2 mt-2 space-y-1.5" data-testid="sign-review-revisions">
                        <div className="font-medium">Open patient change request(s): {openRevisions.length}</div>
                        {openRevisions.map((r) => <div key={r.id} className="text-xs">• {r.message}</div>)}
                        <label className="flex items-start gap-2 pt-1">
                          <Checkbox checked={ackRevisions} onCheckedChange={(v) => setAckRevisions(!!v)} data-testid="doctor-ack-revisions-checkbox" />
                          <span className="text-xs">I have reviewed these request(s) and confirm the packet is correct as-is.</span>
                        </label>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="attest">
                  <AccordionTrigger>2. Attestation</AccordionTrigger>
                  <AccordionContent>
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox checked={attest} onCheckedChange={(v) => setAttest(!!v)} data-testid="doctor-attest-checkbox" />
                      <span>I have reviewed the patient information, clinical details, and recovery recommendations. I certify under penalty of perjury that the information is true and correct to the best of my professional medical knowledge.</span>
                    </label>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="sign">
                  <AccordionTrigger>3. Sign</AccordionTrigger>
                  <AccordionContent>
                    {/* The typed-signature default is the signer, never a seed-data name. */}
                    <SignaturePad onChange={setSignatureDataUrl} defaultName={user?.name || ''} />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="deliver">
                  <AccordionTrigger>4. Delivery</AccordionTrigger>
                  <AccordionContent className="text-sm space-y-1">
                    <div>Method: <strong>{sub.delivery?.method || 'Email'}</strong></div>
                    <div>Employer email: {sub.delivery?.employer_email || '—'}</div>
                    <div>Employer fax: {sub.delivery?.employer_fax || '—'}</div>
                    <label className="flex items-start gap-2 text-sm pt-2">
                      <Checkbox checked={saveTemplate} onCheckedChange={(v) => setSaveTemplate(!!v)} data-testid="doctor-save-template-checkbox" />
                      <span>Save my AI recovery-week edit back to the procedure catalog template (forward-looking only).</span>
                    </label>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <DialogFooter>
                <Button onClick={sign} disabled={busy || !signatureDataUrl || !attest} data-testid="doctor-sign-confirm-button"><Send className="h-4 w-4 mr-1" /> Apply signature & deliver</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {sub.decline && (
        <div className="rounded-lg border bg-muted p-3 mb-4 flex items-start gap-2" data-testid="doctor-decline-banner">
          <Ban className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">Not certified — decision recorded by {sub.decline.by}</div>
            {/* Labelled from the same fetched list the picker uses — a second copy
                here would drift the moment the config changes. */}
            <div>{declineReasons.find((r) => r.code === sub.decline.reason_code)?.label || sub.decline.reason_code}</div>
            {sub.decline.note && <div className="text-sm text-muted-foreground mt-0.5">{sub.decline.note}</div>}
          </div>
        </div>
      )}

      {sub.nurse_note_to_doctor && (
        <div className="rounded-lg border bg-accent text-accent-foreground p-3 mb-4 flex items-start gap-2" data-testid="doctor-nurse-note">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div><div className="font-medium">Medical staff note:</div><div>{sub.nurse_note_to_doctor}</div></div>
        </div>
      )}

      {openQuestions.length > 0 && (
        <div className="rounded-lg border bg-muted/40 p-3 mb-4 text-sm" data-testid="doctor-question-panel">
          <div className="font-medium mb-1">Your question{openQuestions.length > 1 ? 's' : ''} to the patient</div>
          <ul className="space-y-2">
            {openQuestions.map((q) => (
              <li key={q.id}>
                <div className="text-muted-foreground">{q.message}</div>
                <div className="mt-0.5" data-testid="doctor-question-reply">{q.patient_reply
                  ? <><span className="font-medium">Patient:</span> {q.patient_reply}</>
                  : <span className="text-xs text-muted-foreground">Waiting for the patient to answer.</span>}</div>
              </li>
            ))}
          </ul>
          <div className="text-xs text-muted-foreground mt-1">Signing closes the question.</div>
        </div>
      )}

      {openRevisions.length > 0 && (
        <div className="rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] p-3 mb-4 flex items-start gap-2" data-testid="doctor-revision-banner">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-medium">The patient requested {openRevisions.length === 1 ? 'a change' : `${openRevisions.length} changes`} on this packet:</div>
            <ul className="list-disc ml-4 mt-0.5">
              {openRevisions.map((r) => <li key={r.id}>{r.message}</li>)}
            </ul>
            <div className="text-xs mt-1">{amending
              ? 'This packet was already delivered. Correct what needs correcting here, then sign the amended packet and acknowledge the request(s) in the signing dialog.'
              : 'Return to the medical staff to fix it, or acknowledge the request(s) in the signing dialog if the packet is correct as-is.'}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Section title="Patient information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[['Full name', sub.patient_form?.full_name], ['DOB', sub.patient_form?.dob], ['Employer', sub.patient_form?.employer_name], ['Job title', sub.patient_form?.job_title], ['Reason', sub.patient_form?.reason_for_leave], ['Leave', `${sub.patient_form?.leave_start || ''} → ${sub.patient_form?.leave_end || ''}`]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div>{v || '—'}</div></div>
              ))}
            </div>
          </Section>
          <Section title="Clinical (Medical staff)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[['ICD-10', sub.nurse_clinical?.icd10], ['CPT', sub.nurse_clinical?.cpt], ['Diagnosis', sub.nurse_clinical?.diagnosis_description], ['Procedure', sub.nurse_clinical?.procedure_name], ['Procedure date', sub.nurse_clinical?.procedure_date], ['Severity', sub.nurse_clinical?.severity], ['Incapacity', sub.nurse_clinical?.incapacity_type]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-muted-foreground">{k}</div><div>{v || '—'}</div></div>
              ))}
              <div className="md:col-span-2"><div className="text-xs text-muted-foreground">Clinical notes</div><div>{sub.nurse_clinical?.clinical_notes || '—'}</div></div>
            </div>
          </Section>
          <Section title="Functional capacity">
            <div className="text-sm overflow-x-auto">
              <table className="w-full text-sm border rounded-lg">
                <thead className="bg-secondary">
                  <tr><th className="px-2 py-1 text-left">Metric</th><th className="px-2 py-1">Now</th><th className="px-2 py-1">30d</th><th className="px-2 py-1">60d</th><th className="px-2 py-1">RTW</th></tr>
                </thead>
                <tbody>
                  {[['Sitting', 'sitting'], ['Standing', 'standing'], ['Walking', 'walking'], ['Lifting (lbs)', 'lifting_lbs'], ['Driving', 'driving']].map(([l, k]) => (
                    <tr key={k} className="border-t">
                      <td className="px-2 py-1 text-muted-foreground">{l}</td>
                      {['current', 'd30', 'd60', 'rtw'].map(c => <td key={c} className="px-2 py-1 text-center">{sub.functional_capacity?.[c]?.[k] ?? '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          {sub.uploads?.length > 0 && (
            <Section title="Paper records / uploads">
              <div className="grid grid-cols-2 gap-3">
                {(sub.uploads || []).map(u => (
                  <button key={u.id} type="button" onClick={() => openAuthedFile(`/uploads/${sid}/${u.id}`)} className="rounded-lg border p-2 text-xs hover:bg-muted text-left w-full" data-testid={`doctor-upload-open-${u.id}`}>
                    <div className="truncate font-medium">{u.name}</div>
                    <div className="text-muted-foreground">{u.content_type} · {(u.size/1024).toFixed(1)} KB</div>
                  </button>
                ))}
              </div>
            </Section>
          )}
          {sub.delivery_log?.length > 0 && (
            <Section title="Delivery log">
              <ul className="text-sm space-y-1">
                {sub.delivery_log.map((d, i) => (
                  <li key={`${d.event}-${d.at || i}`} className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-primary" /> <span className="font-medium">{d.event}</span> <span className="text-muted-foreground">{d.to ? `→ ${d.to}` : ''} {d.simulated && <em>(simulated)</em>}</span></li>
                ))}
              </ul>
            </Section>
          )}
        </div>
        <div className="space-y-4">
          <AIPanel
            ai={sub.ai_recommendations}
            original={sub.ai_recommendations_original}
            mode={(practice?.settings || {}).ai_mode || 'real'}
            onGenerate={generate}
            onChange={updateAi}
            busy={busy}
          />
          {sub.ai_recommendations && (decidable || amending) && (
            <Button variant="outline" onClick={saveAi} className="w-full" data-testid="doctor-save-ai-edits">Save AI edits</Button>
          )}
          <AddendumPreview sid={sid} employerForm={sub.employer_form} />
        </div>
      </div>
    </div>
  );
}
